import { I18n } from "@assets/strings";
import { Range } from "immutable";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Feedback } from "@domain/group/feedback/feedback";
import { Group } from "@domain/group/group";
import { fullName } from "@domain/user/utils";
import { QuoteIcon } from "@ui/components/icons";
import { Row, Stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { gap } from "@ui/styles/utils";
import { Star } from "@ui/pages/lms/coursePage/feedback/star";
import { FeedbackModal } from "./feedbackModal";

interface FeedbackViewProps extends React.HTMLAttributes<HTMLDivElement> {
	feedback: Feedback;
	shareroom: Group;
}
export const FeedbackView: React.FC<FeedbackViewProps> = ({ feedback, shareroom, ...htmlProps }) => {
	const { user, score, text, lastUpdateDate } = feedback;
	const currentProfile = useCurrentProfile();
	const { groupService } = useServices();
	const { createModal } = useModalTools();
	const askDeleteConfirmation = useConfirmation(
		I18n.t("shareroom.delete_feedback.title"),
		I18n.t("delete"),
		I18n.t("shareroom.delete_feedback.description")
	);
	const isAuthor = currentProfile?.id === user.id;

	return (
		<Container {...htmlProps}>
			<FeedbackContainer>
				<Link to={Path.Profile(user)}>
					<ProfilePicture picture={user.avatar} size={40} />
				</Link>
				<FeedbackContent>
					<Link to={Path.Profile(user)}>
						<Author>{fullName(user)}</Author>
					</Link>
					{user.description && <Description>{user.description}</Description>}
					<Score>
						<Bloc style={{ gap: "8px" }}>
							<Row>
								{Range(1, 6).map((n) => (
									<Star key={n} $isFilled={score >= n} />
								))}
							</Row>
							<div>{score}</div>
						</Bloc>
					</Score>
					<Bloc>{text ? <Text>{text}</Text> : null}</Bloc>
					<Date>
						<div>{I18n.t("feedback.date", { date: lastUpdateDate.toLocaleDateString() })}</div>
					</Date>
				</FeedbackContent>
				{currentProfile && (
					<FeedbackMenu>
						<SimpleMenuContent
							onEdit={
								isAuthor
									? () =>
											createModal((close) => <FeedbackModal feedback={feedback} close={close} shareroom={shareroom} />)
									: undefined
							}
							onDelete={
								isAuthor
									? () =>
											askDeleteConfirmation(async () => {
												await groupService.deleteFeedback(shareroom.id, feedback.id);
											})
									: undefined
							}
							reportInfos={
								isAuthor
									? undefined
									: {
											contentId: shareroom.id,
											contentType: "GROUP",
										} // TODO REPLACE WITH FEEDBACK ID
							}
						/>
					</FeedbackMenu>
				)}
			</FeedbackContainer>
			<Separator />
		</Container>
	);
};

interface CustomFeedbackViewProps extends Omit<FeedbackViewProps, "shareroom"> {
	shareroom?: Group;
}

export const CustomFeedbackView: React.FC<CustomFeedbackViewProps> = ({ feedback, ...htmlProps }) => {
	const { user, score, text } = feedback;
	return (
		<Container {...htmlProps}>
			<Stack justify="center" align="center">
				<FeedbackContainer>
					<Link to={Path.Profile(user)}>
						<ProfilePicture picture={user.avatar} size={40} />
					</Link>
					<FeedbackContent>
						<Link to={Path.Profile(user)}>
							<Author>{fullName(user)}</Author>
						</Link>
						{user.description && <Description>{user.description}</Description>}
						<Score>
							<Bloc style={{ gap: "8px" }}>
								<Row>
									{Range(1, 6).map((n) => (
										<Star key={n} $isFilled={score >= n} />
									))}
								</Row>
								<div>{score}</div>
							</Bloc>
						</Score>
					</FeedbackContent>
				</FeedbackContainer>
				<CustomBloc>
					<QuoteIcon />
					{text ? <Text>{text}</Text> : null}
				</CustomBloc>
			</Stack>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
`;

const FeedbackContainer = styled.div`
	padding: 16px;
	display: flex;
	position: relative;

	> :first-child {
		margin-right: 16px;
	}
`;

const Description = styled.div`
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 16px;
`;

const Text = styled.div`
	font-weight: 400;
	font-size: 16px;
	line-height: 24px;

	letter-spacing: -0.02em;
`;

const FeedbackContent = styled.div`
	flex: 1;
`;

const Author = styled.b``;

const Bloc = styled.div`
	margin-top: 5px;
	display: flex;
	align-items: center;
	justify-content: space-between;

	@media (max-width: 900px) {
		flex-direction: row;
		align-items: center;
	}
`;
const CustomBloc = styled.div`
	width: 50%;
	display: flex;
	position: relative;
	> svg {
		position: absolute;
		left: -70px;
	}
`;
const Date = styled.div`
	margin-top: 5px;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	${({ theme }) => theme.textStyles.label};
	color: ${({ theme }) => theme.colors.textTertiary};
	font-weight: 400;

	@media (max-width: 900px) {
		flex-direction: column;
		align-items: flex-end;
	}
`;

const FeedbackMenu = styled(KebabMenu)`
	position: absolute;
	right: 10px;
	top: 10px;
	background-color: #f3f4f6;
`;

const Score = styled.div`
	display: flex;
	align-items: center;
	${gap(6, "row")}
	svg {
		width: 18px;
	}
	@media (max-width: 900px) {
		align-self: flex-start;
	}
`;
const Separator = styled.div`
	background-color: rgba(0, 0, 0, 0.1);
	height: 1px;
	width: 100%;
	opacity: 0.5;
`;
// const NoLiveBloc = styled.div`
// 	${stack(20, "center", "center")};
// 	width: 100%;
// 	> img {
// 		width: 400px;
// 	}
// `;
// const EmptyMessage = styled.div`
// 	font-weight: 400;
// 	font-size: 16px;
// 	line-height: 26px;
// 	color: rgba(1, 1, 1, 0.5);
// 	margin-top: 20px;
// 	@media ${mobile} {
// 		font-size: 13px;
// 	}
// `;
